// Localização: frontend/src/pages/Assinatura/SemAssinaturaPage.jsx
// Página para usuários sem assinatura ativa - mostra vantagens do sistema

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    CheckCircle2, 
    ArrowRight, 
    Zap, 
    Shield, 
    BarChart3,
    Users,
    Calendar,
    FileText,
    Pill,
    Package,
    Settings,
    LogOut
} from 'lucide-react';
import HeaderPublic from '../../components/Navigation/HeaderPublic';
import Footer from '../../components/LandingPage/Footer';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { useAuth } from '../../context/AuthContext';

const SemAssinaturaPage = () => {
    const navigate = useNavigate();
    const { subscription, hasActiveSubscription, fetchSubscription, user, handleLogout } = useAuth();
    
    // ✅ CORREÇÃO: Admins globais não devem ficar presos nesta página
    // Redirecionar automaticamente para dashboard se for admin
    useEffect(() => {
        const isGlobalAdmin = user && ['global_admin', 'super_admin', 'admin'].includes(user.role);
        if (isGlobalAdmin) {
            navigate('/dashboard', { replace: true });
        }
    }, [user, navigate]);
    
    // Função para sair e redirecionar para página de recursos
    const handleSair = () => {
        handleLogout();
        navigate('/recursos');
    };
    
    // Verificar periodicamente se a assinatura foi ativada
    useEffect(() => {
        let isMounted = true;
        let checkCount = 0;
        const maxChecks = 30; // Máximo de 30 verificações (90 segundos)
        let intervalId = null;
        let initialTimeout = null;
        
        // Verificar assinatura
        const verificarAssinatura = async () => {
            if (!isMounted || checkCount >= maxChecks) {
                if (checkCount >= maxChecks) {
                    if (intervalId) clearInterval(intervalId);
                }
                return;
            }
            
            checkCount++;
            
            try {
                await fetchSubscription();
            } catch (error) {
                // Se o backend não está disponível, parar o loop
                if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error') || error.message.includes('CONNECTION_REFUSED')) {
                    if (intervalId) clearInterval(intervalId);
                    if (initialTimeout) clearTimeout(initialTimeout);
                    return;
                }
            }
        };

        // Verificar imediatamente na primeira vez
        verificarAssinatura();

        // Aguardar um pouco antes da próxima verificação
        initialTimeout = setTimeout(() => {
            if (isMounted) {
                verificarAssinatura();
            }
        }, 2000);

        // Verificar a cada 3 segundos (menos frequente para evitar sobrecarga)
        intervalId = setInterval(() => {
            if (isMounted && checkCount < maxChecks) {
                verificarAssinatura();
            } else if (checkCount >= maxChecks && intervalId) {
                clearInterval(intervalId);
            }
        }, 3000);

        return () => {
            isMounted = false;
            if (initialTimeout) clearTimeout(initialTimeout);
            if (intervalId) clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Executar apenas uma vez na montagem

    // Redirecionar quando assinatura for detectada
    useEffect(() => {
        const processarAssinatura = async () => {
            if (!subscription) return;

            // Se a assinatura está ATIVA ou PENDENTE (aguardando webhook), redirecionar
            const isActive = hasActiveSubscription();
            const isPendingStripe = subscription.status === 'PENDENTE' && 
                                   subscription.stripe_subscription_id && 
                                   !subscription.stripe_subscription_id.startsWith('TRIAL_');
            
            if (isActive || isPendingStripe) {
                setTimeout(() => {
                    navigate('/dashboard', { replace: true });
                }, 500);
            }
        };

        processarAssinatura();
    }, [subscription, hasActiveSubscription, navigate]);

    const features = [
        {
            icon: BarChart3,
            title: 'Dashboard Completo',
            description: 'Acompanhe KPIs, métricas de saúde e indicadores financeiros em tempo real'
        },
        {
            icon: Users,
            title: 'Gestão de Pacientes',
            description: 'Prontuários eletrônicos completos com histórico médico e evoluções'
        },
        {
            icon: Calendar,
            title: 'Agendamentos Inteligentes',
            description: 'Sistema de agendamento com lembretes automáticos e gestão de horários'
        },
        {
            icon: FileText,
            title: 'Diário de Cuidados',
            description: 'Registro detalhado de todas as atividades e cuidados prestados'
        },
        {
            icon: Pill,
            title: 'Controle de Medicamentos',
            description: 'Gestão completa de estoque, lotes e administração de medicamentos'
        },
        {
            icon: Package,
            title: 'Controle de Estoque',
            description: 'Acompanhamento em tempo real de medicamentos e materiais'
        },
        {
            icon: Settings,
            title: 'Configurações Avançadas',
            description: 'RBAC completo, permissões personalizadas e controle de acesso'
        },
        {
            icon: Shield,
            title: 'Segurança Total',
            description: 'Conformidade com LGPD e proteção de dados sensíveis'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Header Público */}
            <div className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <HeaderPublic />
                </div>
            </div>

            {/* Conteúdo Principal */}
            <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6">
                        <Zap className="h-10 w-10 text-emerald-600" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        Desbloqueie Todo o Potencial do Higeia
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Sua organização ainda não possui uma assinatura ativa. Escolha um plano e comece a aproveitar todos os recursos da plataforma.
                    </p>
                </div>

                {/* Seção de Recursos */}
                <div className="mb-12">
                    <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
                        O que você pode fazer com o Higeia
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div
                                    key={index}
                                    className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow"
                                >
                                    <div className="flex items-center mb-4">
                                        <div className="p-2 bg-emerald-100 rounded-lg">
                                            <Icon className="h-6 w-6 text-emerald-600" />
                                        </div>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {feature.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                    {/* Botão CTA */}
                    <div className="text-center">
                        <PrimaryButton
                            onClick={() => navigate('/pricing')}
                            className="!bg-emerald-600 hover:!bg-emerald-800 active:!bg-emerald-900 !text-white hover:!text-white active:!text-white px-8 py-4 text-lg !border-emerald-600 hover:!border-emerald-800 active:!border-emerald-900 !shadow-md hover:!shadow-lg transition-all duration-200 hover:!bg-gradient-to-br hover:!from-emerald-800 hover:!to-emerald-900"
                        >
                            Ver Planos e Preços
                            <ArrowRight className="h-5 w-5 ml-2" />
                        </PrimaryButton>
                        <p className="text-sm text-gray-500 mt-4">
                            Escolha o plano ideal para sua organização e comece hoje mesmo
                        </p>
                    </div>
                </div>

                {/* Benefícios */}
                <div className="bg-emerald-50 rounded-2xl p-8 mb-12">
                    <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
                        Por que escolher o Higeia?
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                            <h3 className="font-semibold text-gray-900 mb-2">
                                Economia de Tempo
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Automatize tarefas repetitivas e foque no que realmente importa: cuidar dos pacientes.
                            </p>
                        </div>
                        <div className="text-center">
                            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                            <h3 className="font-semibold text-gray-900 mb-2">
                                Dados em Tempo Real
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Acompanhe indicadores de saúde e métricas financeiras em dashboards interativos.
                            </p>
                        </div>
                        <div className="text-center">
                            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                            <h3 className="font-semibold text-gray-900 mb-2">
                                Segurança Garantida
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Conformidade total com LGPD e proteção de dados sensíveis dos pacientes.
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            {/* Botão de Sair - Acima do Footer */}
            <div className="flex justify-end pr-4 sm:pr-6 lg:pr-8 pb-4">
                <button
                    onClick={handleSair}
                    className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors duration-200 font-medium"
                    title="Sair da Aplicação"
                >
                    <LogOut className="h-5 w-5" />
                    <span>Sair</span>
                </button>
            </div>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default SemAssinaturaPage;

